import { useState } from 'react';
import { FaHeartbeat } from 'react-icons/fa';
import blueHeart from '@/assets/blue-heart.png';
import stethoscopeIcon from '@/assets/stethoscope-icon.png';
import insanIcon from '@/assets/insan.png';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { Strings } from '@/constants/strings';
import { LegalInfoModal, AlgorithmInfoModal } from '@/components';
import type { UserMode } from '@/components/RiskCalculator';

interface HomeProps {
  onSelectMode: (mode: UserMode) => void;
}

export function Home({ onSelectMode }: HomeProps) {
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [algorithmModalOpen, setAlgorithmModalOpen] = useState(false);

  return (
    <div className="flex min-h-screen-safe flex-col bg-surface px-6 pb-10 pt-8 sm:pt-12">
      {/* Hero Section - Centered & Sleek */}
      <header className="mb-10 text-center">
        <div className="mb-4 flex justify-center" aria-hidden>
          <img
            src={blueHeart}
            alt=""
            className="animate-clinical-pulse"
            width={80}
            height={80}
            style={{ width: 80, height: 'auto' }}
          />
        </div>
        <h1 className="mb-3 text-2xl font-bold tracking-tight text-text-heading sm:text-3xl">
          Kardiyovasküler Sağlık Paneli
        </h1>
        <p className="mx-auto max-w-xl text-base leading-relaxed text-text-muted sm:text-lg">
          {Strings.heroSubheadline}
        </p>
      </header>

      {/* Professional Disclaimer */}
      <p className="mb-8 text-center text-sm leading-relaxed text-text-muted">
        {Strings.medicalDisclaimer}
      </p>

      {/* Role Selection - Modern Cards */}
      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={() => onSelectMode('healthcare')}
          className="group relative flex items-center overflow-visible rounded-3xl border border-slate-100 bg-card-bg p-6 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
        >
          <img
            src={stethoscopeIcon}
            alt=""
            className="absolute top-1/2 z-10 object-contain opacity-80 transition-opacity group-hover:opacity-100"
            width={80}
            height={80}
            style={{
              height: 80,
              width: 'auto',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              imageRendering: 'crisp-edges',
            }}
          />
          <span className="relative block text-xl font-bold text-primary">
            {Strings.iAmHealthcare}
          </span>
        </button>

        <button
          type="button"
          onClick={() => onSelectMode('public')}
          className="group relative flex items-center overflow-visible rounded-3xl border border-slate-100 bg-card-bg p-6 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
        >
          <img
            src={insanIcon}
            alt="Kullanıcı İkonu"
            className="absolute right-[3.25rem] top-1/2 z-10 h-10 w-10 shrink-0 -translate-y-1/2 object-contain opacity-80 transition-opacity group-hover:opacity-100"
          />
          <span className="relative block text-lg font-bold text-text-heading">
            {Strings.iAmNotHealthcare}
          </span>
        </button>
      </div>

      <button
        type="button"
        onClick={() => setAlgorithmModalOpen(true)}
        className="mt-6 flex items-center justify-center gap-2 text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label="Kullanılan yöntemler hakkında"
      >
        <span className="text-base" aria-hidden>ℹ️</span>
        <span className="text-sm font-medium">Kullanılan Yöntemler</span>
      </button>

      <button
        type="button"
        onClick={() => setLegalModalOpen(true)}
        className="mt-auto flex items-center justify-center gap-2 pt-8 pb-6 text-text-muted hover:text-text-heading focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-label="Hakkında ve yasal uyarı"
      >
        <FaHeartbeat className="h-5 w-5 shrink-0 text-primary" aria-hidden />
        <IoInformationCircleOutline className="h-5 w-5 shrink-0" aria-hidden />
        <span className="text-xs">Hakkında ve Yasal Uyarı</span>
      </button>

      <AlgorithmInfoModal visible={algorithmModalOpen} onClose={() => setAlgorithmModalOpen(false)} />
      <LegalInfoModal visible={legalModalOpen} onClose={() => setLegalModalOpen(false)} />
    </div>
  );
}
